import {
  cloneElement,
  useCallback,
  useMemo,
  useRef,
  ComponentType,
  PropsWithRef,
  useEffect,
  Children,
} from 'react';
import { useClickAway } from 'react-use';

import {
  ENTER_KEY,
  ESCAPE_KEY,
  SPACE_KEY,
  UP_ARROW,
  DOWN_ARROW,
  END,
  HOME,
  TAB_KEY,
} from 'constants/keyCodes';
import { BUTTON_SIZES } from 'ui/button/constants';
import { track } from 'modules/segment/segmentEmitter';

import { CheckableOption } from './CheckableOption';
import { ExpandableCategoryGroup as DefaultExpandableCategoryGroup } from './ExpandableCategoryGroup';
import {
  useAriaSelectProps,
  useCategorySelection,
  useFilterText,
  useSelectOpenTracking,
} from './hooks/index';
import {
  CategoryGroup,
  CategoryHeader,
  CategoryLabel,
  ClearButton,
  DoubleLine,
  GhostText,
  OptionLabel,
  OptionsContainer,
  OptionWrapper,
  SelectContainer,
  SelectDropdownInfoText,
  SelectWrapper,
  TriggerContainer,
} from './Selects.styles';
import { LoadingOption } from './LoadingOption';
import { SelectValueDisplay } from './SelectValueDisplay';
import {
  SelectProps,
  MultiSelectProps,
  SelectValueType,
  SelectValueDisplayProps,
  BasicOptionProps,
} from './types';
import {
  clickAwayHandler,
  createDropdownToggle,
  createUniqueOptionsAndDefaults,
  getMaxReachedProps,
} from './utils';
import { DEFAULT_CATEGORY_ID, SELECTED_OPTIONS_CATEGORY_ID } from './constants';

export const MULTI_SELECT_EMPTY_TEXT_TEST_ID = 'multi-select-empty-text-test-id';

const baseContent = {
  clearText: 'Clear',
  selectAll: 'Select all',
  selectNone: 'Select none',
};
/** This is a DUPLICATE, but storybook won't import the types */
/** MultiSelect uses single ReactElement */

/**
 * THIS IS DEPRECATED, Please use src/ui/multiselectv2/MultiSelect.tsx
 */

export type MultiSelectComponentProps<TValue extends SelectValueType = string> =
  SelectProps<TValue> & MultiSelectProps;

export function MultiSelect<TValue extends SelectValueType = string>({
  categoryLabels = {},
  children,
  content = baseContent,
  defaultValues = [], // setting this to a string will break option selection
  displayContent = {
    multipleSelectedText: '',
  },
  ExpandableCategoryGroup = DefaultExpandableCategoryGroup,
  groupBySelectedOptions = false,
  groupSelectedInCategory = false,
  id = '',
  isClearable = false,
  isDisabled = false,
  isError = false,
  isRequired = false,
  isReadOnly = false,
  isSegmentTracked = false,
  hasSelectAllOption = false,
  labelId = '',
  onSearchChange,
  onSelect = () => undefined,
  Option = CheckableOption as ComponentType<React.PropsWithChildren<BasicOptionProps<TValue>>>,
  options = [],
  placeholder = '',
  segmentProps,
  title = '',
  emptyStateText = 'No options available',
  isEmbedded = false,
  isExpandable = false,
  optionWrapperWidth = '',
  showAllCategories = false,
  selectValueDisplayText = '',
  Trigger = SelectValueDisplay as ComponentType<
    React.PropsWithChildren<PropsWithRef<SelectValueDisplayProps<TValue>>>
  >,
  isLoading = false,
  autoFocusOnInput = false,
  isFullWidth = false,
  max = Infinity,
  disableInternalFiltering = false,
  ...props
}: MultiSelectComponentProps<TValue>) {
  const isExternalSearchUsed = disableInternalFiltering;
  /** Refs - used for focus tracking */
  const parentEl = useRef<HTMLDivElement>(null); // a ref for the wrapper
  const triggerEl = useRef<HTMLButtonElement>(null); // the trigger for the select
  const optionsEl = useRef<HTMLDivElement & HTMLUListElement>(null); // the wrapper of the options

  /**
    If a child is provided it's going to be some kind of filter
    This tracks the state of its change function
   */
  const [filterText, setFilterText] = useFilterText('');

  /**
    Return the options and the defaultOptions as uniques, the memoization
    here is critical to control state thrashing in the useOptionsSelection
    hooks
    new optionId = parentId + "-" + optionId
    new defaultIds = parentId + "-" + defaultId
  */
  const [uniqueOptions, uniqueDefaults] = useMemo(
    () => createUniqueOptionsAndDefaults<TValue>(id, options, defaultValues),
    [id, options, defaultValues],
  );

  /** see documentation inside useCategorySelection.js */
  const selectionState = useCategorySelection({
    callback: onSelect,
    defaultSelections: uniqueDefaults,
    filterText,
    options: uniqueOptions,
    isExternalSearchUsed,
    showAllCategories,
    hasSelectAllOption,
    groupBySelectedOptions,
    groupSelectedInCategory,
  });
  const {
    categoryDetail,
    clearAll,
    getOptions,
    groupAndSortBySelectedOptions,
    selectAllInCategory,
    selectNoneInCategory,
    selectAll,
    selectNone,
    selectedOptions,
    showHeaders,
    toggle,
  } = selectionState;

  /** Build out the associated ARIA props by the state of the component */
  const ariaProps = useAriaSelectProps({
    // if a single node is active, it becomes the activeId on the listbox
    activeId: selectedOptions.length === 1 ? selectedOptions[0].id : '',
    isDisabled,
    isReadOnly,
    isRequired,
    labelId,
    title,
  });

  const maxIsReached = Number.isFinite(max) && selectedOptions.length >= max;

  // We also are going to block interactivity until we ensure that
  // options are present should protect against downstream errors
  // from waiting on async options loading
  //
  // The exception is when we are using external search, as empty options may
  // just mean that the value they're searching by yeilded no results
  const isNonInteractive = isDisabled || isReadOnly || (!isExternalSearchUsed && !options.length);

  /**
    This tracks the open/closed status for the select, and handles focus
    no selection: focuses first item
    single selection: focuses that selection
    multiples selections: focuses first selected option
   */
  const [isOpen, setIsOpen] = useSelectOpenTracking({
    optionsEl,
    selectedOptions: selectedOptions.map((option) => `${option.value}`),
    triggerEl,
    autoFocusOnInput,
  });

  useEffect(() => {
    /**
     * We group and sort our options based on the groupBySelectedOptions preference and
     * groupSelectedInCategory will sort options into a temporary "selected" category
     * when the user closes the multi-select. Since, grouping and sorting while the menu
     * is open can be a jarring user experience.
     */
    if ((groupBySelectedOptions || groupSelectedInCategory) && !isOpen) {
      groupAndSortBySelectedOptions();
    }
  }, [isOpen]);

  const trackToggle = () => {
    if (isSegmentTracked) {
      const trackingProps =
        segmentProps?.TOGGLE ??
        (['', {}, {}, () => undefined] as [
          string,
          Record<string, unknown>,
          Record<string, unknown>,
          () => void,
        ]);
      track(...trackingProps);
    }
  };

  const toggleDropdown = createDropdownToggle(setIsOpen, isOpen, trackToggle);

  useClickAway(parentEl, () => {
    clickAwayHandler(toggleDropdown);
  });

  // this cb executes a fn if any of the listed keys match the keycode
  // documention on required ARIA keyboard interactions here
  // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/listbox_role
  const handleKeyPress = useCallback((e: any) => {
    const { keyCode } = e;
    try {
      /**
      Multiselects have complex child structures that include non-option focusable
      buttons like "select all"
      */
      const focusableItems = Array.from(
        optionsEl.current?.querySelectorAll(`input[type="text"],[tabindex='0']`) || [],
      ).map((child) => ({
        el: child as HTMLElement,
        id: child.getAttribute('id'),
        isFocused: child === document.activeElement,
      }));
      const focusedOptionIndex = focusableItems.findIndex((option) => option.isFocused);
      const [firstOption, lastOption] = [
        focusableItems[0],
        focusableItems[focusableItems.length - 1],
      ];

      const isItemFocused =
        Boolean(document.activeElement) &&
        focusableItems.map((option) => option.el).includes(document.activeElement as HTMLElement);
      const isFirstItemFocused = firstOption.el === document.activeElement;
      const isLastItemFocused = lastOption.el === document.activeElement;
      const isShiftKeyPressed = e.shiftKey;

      switch (keyCode) {
        case END: {
          lastOption.el.focus();
          break;
        }

        case HOME: {
          firstOption.el.focus();
          break;
        }

        case ESCAPE_KEY: {
          toggleDropdown(false);
          break;
        }

        case TAB_KEY: {
          if (isItemFocused && isLastItemFocused && !isShiftKeyPressed) {
            e.preventDefault(); // interrupt the tab key
            firstOption.el.focus(); // cycle back to the first element
          }

          if (isItemFocused && isFirstItemFocused && isShiftKeyPressed) {
            e.preventDefault(); // interrupt the tab key
            lastOption.el.focus(); // cycle back to the last element
          }
          break;
        }

        case UP_ARROW: {
          if (isItemFocused && !isFirstItemFocused) {
            focusableItems[focusedOptionIndex - 1].el.focus();
          } else {
            lastOption.el.focus();
          }
          break;
        }

        case DOWN_ARROW: {
          if (!isItemFocused || isLastItemFocused) {
            firstOption.el.focus();
          } else {
            focusableItems[focusedOptionIndex + 1].el.focus();
          }
          break;
        }

        default: {
          return;
        }
      }
    } catch (err) {
      console.error(err); // eslint-disable-line
    }
  }, []);

  const showSelectAllOption =
    hasSelectAllOption &&
    !!categoryDetail?.[DEFAULT_CATEGORY_ID] &&
    !isLoading &&
    getOptions(DEFAULT_CATEGORY_ID, filterText).length > 0;
  const handleSelectAll = (areAllSelected: boolean) => {
    if (areAllSelected) {
      selectNone(filterText);
    } else {
      selectAll(filterText);
    }
  };

  const showEmptyState = !getOptions(undefined, filterText).length && !isLoading;

  return (
    <SelectContainer
      data-test={id ? `multi-select-${id}` : 'multi-select'}
      data-select-type="multi"
      id={id}
      ref={parentEl}
      isFullWidth={isFullWidth}
    >
      {!isEmbedded && (
        <TriggerContainer>
          {isClearable && (
            <ClearButton data-test="multi-select-clear-button" onClick={clearAll}>
              {content.clearText}
            </ClearButton>
          )}
          <SelectWrapper
            data-test={`multi-select-trigger-${id}`}
            id={`trigger-${id}`}
            isError={isError}
            isNonInteractive={isNonInteractive}
            ref={parentEl}
            isFullWidth={isFullWidth}
            {...props}
          >
            <Trigger
              content={displayContent}
              data-test="multi-select-value-display"
              isDisabled={isDisabled}
              isError={isError}
              isOpen={isOpen}
              isNonInteractive={isNonInteractive}
              isReadOnly={isReadOnly}
              placeholder={placeholder}
              ref={triggerEl}
              selectedOptions={selectedOptions}
              toggleDropdown={toggleDropdown}
              selectValueDisplayText={selectValueDisplayText}
              isLoading={isLoading}
            />
          </SelectWrapper>
        </TriggerContainer>
      )}
      {isOpen && !isNonInteractive && (
        <OptionsContainer {...props} {...ariaProps}>
          <OptionWrapper
            data-test="listbox"
            as="div"
            onKeyDown={handleKeyPress}
            ref={optionsEl}
            role="listbox"
            isEmbedded={isEmbedded}
            width={optionWrapperWidth}
            isFullWidth={isFullWidth}
            {...ariaProps}
          >
            {children &&
              Children.map(children, (child) =>
                cloneElement(child, {
                  ...child.props,
                  onChange: (newValue: string) => {
                    onSearchChange?.(newValue);
                    setFilterText(newValue);
                  },
                  value: filterText,
                }),
              )}
            {showEmptyState && (
              <SelectDropdownInfoText data-test={MULTI_SELECT_EMPTY_TEXT_TEST_ID}>
                {emptyStateText}
              </SelectDropdownInfoText>
            )}
            {showSelectAllOption && (
              <Option
                aria-selected={categoryDetail[DEFAULT_CATEGORY_ID].areAllOptionsSelected}
                as="div"
                data-test={`${id}-selectAllOption`}
                key={'selectAll'}
                onKeyDown={({ keyCode }) => {
                  if ([ENTER_KEY, SPACE_KEY].includes(keyCode)) {
                    handleSelectAll(categoryDetail[DEFAULT_CATEGORY_ID].areAllOptionsSelected);
                  }
                }}
                isSelected={categoryDetail[DEFAULT_CATEGORY_ID].areAllOptionsSelected}
                onClick={() => {
                  handleSelectAll(categoryDetail[DEFAULT_CATEGORY_ID].areAllOptionsSelected);
                }}
                role="option"
                tabIndex={isOpen ? 0 : undefined}
              >
                Select all
              </Option>
            )}
            {isLoading ? (
              <LoadingOption />
            ) : (
              Object.values(categoryDetail).map(({ areAllOptionsSelected, categoryId }) => {
                const activeOptions = getOptions(categoryId, filterText);

                // eject if there aren't any valid options to display for the category
                // and if showallcategories is false or if category doesnt exist
                if (
                  activeOptions.length < 1 &&
                  (!showAllCategories || !categoryLabels[categoryId])
                ) {
                  return null;
                }

                const ActiveOptions = () => {
                  return (
                    <>
                      {activeOptions.map((option) => {
                        const selectedCategory =
                          groupSelectedInCategory && option.categoryId
                            ? option.categoryId
                            : categoryId;

                        const selectOption = () => {
                          toggle(selectedCategory, option.value);
                        };

                        const maxIsReachedProps = maxIsReached
                          ? getMaxReachedProps(option, content)
                          : {};

                        return (
                          <Option
                            {...option}
                            aria-selected={option.isSelected}
                            aria-disabled={option.isDisabled}
                            as="div"
                            data-test={`${id}-option`}
                            key={option.uniqueId}
                            onKeyDown={({ keyCode }) => {
                              if ([ENTER_KEY, SPACE_KEY].includes(keyCode)) {
                                selectOption();
                              }
                            }}
                            id={option.uniqueId}
                            onClick={selectOption}
                            role="option"
                            tabIndex={isOpen ? 0 : undefined}
                            {...maxIsReachedProps}
                          >
                            <OptionLabel>{option.label}</OptionLabel>
                            {option.doubleLine && (
                              <DoubleLine isSelected={option.isSelected}>
                                {option.doubleLine}
                              </DoubleLine>
                            )}
                          </Option>
                        );
                      })}
                    </>
                  );
                };

                return (
                  <CategoryGroup data-test={`category-group-${categoryId}`} key={categoryId}>
                    {isExpandable ? (
                      <ExpandableCategoryGroup
                        onClick={() => {
                          if (areAllOptionsSelected) {
                            selectNoneInCategory(categoryId, filterText);
                          } else {
                            selectAllInCategory(categoryId, filterText);
                          }
                        }}
                        isSelected={areAllOptionsSelected}
                        label={categoryLabels[categoryId] || categoryId || ''}
                        count={activeOptions.length}
                      >
                        <ActiveOptions />
                      </ExpandableCategoryGroup>
                    ) : (
                      <>
                        {showHeaders && categoryId !== SELECTED_OPTIONS_CATEGORY_ID && (
                          <CategoryHeader>
                            <CategoryLabel>{categoryLabels[categoryId] || ''}</CategoryLabel>
                            {max === Infinity && (
                              <GhostText
                                onClick={() => {
                                  if (areAllOptionsSelected) {
                                    selectNoneInCategory(categoryId, filterText);
                                  } else {
                                    selectAllInCategory(categoryId, filterText);
                                  }
                                }}
                                size={BUTTON_SIZES.SMALL}
                                tabIndex={0}
                              >
                                {areAllOptionsSelected ? content.selectNone : content.selectAll}
                              </GhostText>
                            )}
                          </CategoryHeader>
                        )}
                        <ActiveOptions />
                      </>
                    )}
                  </CategoryGroup>
                );
              })
            )}
          </OptionWrapper>
        </OptionsContainer>
      )}
      {isEmbedded && !isNonInteractive && (
        <OptionWrapper
          data-test="listbox"
          as="div"
          onKeyDown={handleKeyPress}
          ref={optionsEl}
          role="listbox"
          isEmbedded={isEmbedded}
          width={optionWrapperWidth}
          {...ariaProps}
        >
          {children &&
            Children.map(children, (child) =>
              cloneElement(child, {
                ...child.props,
                onChange: (newValue: string) => {
                  onSearchChange?.(newValue);
                  setFilterText(newValue);
                },
                value: filterText,
              }),
            )}

          {showEmptyState && (
            <SelectDropdownInfoText data-test={MULTI_SELECT_EMPTY_TEXT_TEST_ID}>
              {emptyStateText}
            </SelectDropdownInfoText>
          )}
          {showSelectAllOption && (
            <Option
              aria-selected={categoryDetail[DEFAULT_CATEGORY_ID].areAllOptionsSelected}
              as="div"
              data-test={`${id}-selectAllOption`}
              key={'selectAll'}
              onKeyDown={({ keyCode }) => {
                if ([ENTER_KEY, SPACE_KEY].includes(keyCode)) {
                  handleSelectAll(categoryDetail[DEFAULT_CATEGORY_ID].areAllOptionsSelected);
                }
              }}
              isSelected={categoryDetail[DEFAULT_CATEGORY_ID].areAllOptionsSelected}
              onClick={() => {
                handleSelectAll(categoryDetail[DEFAULT_CATEGORY_ID].areAllOptionsSelected);
              }}
              role="option"
              tabIndex={isOpen ? 0 : undefined}
            >
              Select all
            </Option>
          )}
          {isLoading ? (
            <LoadingOption />
          ) : (
            Object.values(categoryDetail).map(({ areAllOptionsSelected, categoryId }) => {
              const activeOptions = getOptions(categoryId, filterText);

              // eject if there aren't any valid options to display for the category
              // and if showallcategories is false or if category doesnt exist
              if (activeOptions.length < 1 && (!showAllCategories || !categoryLabels[categoryId])) {
                return null;
              }

              const ActiveOptions = () => {
                return (
                  <>
                    {activeOptions.map((option) => {
                      const selectedCategory =
                        groupSelectedInCategory && option.categoryId
                          ? option.categoryId
                          : categoryId;

                      const selectOption = () => {
                        toggle(selectedCategory, option.value);
                      };

                      const maxIsReachedProps = maxIsReached
                        ? getMaxReachedProps(option, content)
                        : {};

                      return (
                        <Option
                          {...option}
                          aria-selected={option.isSelected}
                          aria-disabled={option.isDisabled}
                          as="div"
                          data-test={`${id}-option`}
                          key={option.uniqueId}
                          onKeyDown={({ keyCode }) => {
                            if ([ENTER_KEY, SPACE_KEY].includes(keyCode)) {
                              selectOption();
                            }
                          }}
                          id={option.uniqueId}
                          onClick={selectOption}
                          role="option"
                          tabIndex={isOpen ? 0 : undefined}
                          {...maxIsReachedProps}
                        >
                          <OptionLabel>{option.label}</OptionLabel>
                          {option.doubleLine && (
                            <DoubleLine isSelected={option.isSelected}>
                              {option.doubleLine}
                            </DoubleLine>
                          )}
                        </Option>
                      );
                    })}
                  </>
                );
              };

              return (
                <CategoryGroup data-test={`category-group-${categoryId}`} key={categoryId}>
                  {isExpandable ? (
                    <ExpandableCategoryGroup
                      onClick={() => {
                        if (areAllOptionsSelected) {
                          selectNoneInCategory(categoryId, filterText);
                        } else {
                          selectAllInCategory(categoryId, filterText);
                        }
                      }}
                      isSelected={areAllOptionsSelected}
                      label={categoryLabels[categoryId] || categoryId || ''}
                      count={activeOptions.length}
                    >
                      <ActiveOptions />
                    </ExpandableCategoryGroup>
                  ) : (
                    <>
                      {showHeaders && categoryId !== SELECTED_OPTIONS_CATEGORY_ID && (
                        <CategoryHeader>
                          <CategoryLabel>{categoryLabels[categoryId] || ''}</CategoryLabel>
                          {max === Infinity && (
                            <GhostText
                              onClick={() => {
                                if (areAllOptionsSelected) {
                                  selectNoneInCategory(categoryId, filterText);
                                } else {
                                  selectAllInCategory(categoryId, filterText);
                                }
                              }}
                              size={BUTTON_SIZES.SMALL}
                              tabIndex={0}
                            >
                              {areAllOptionsSelected ? content.selectNone : content.selectAll}
                            </GhostText>
                          )}
                        </CategoryHeader>
                      )}
                      <ActiveOptions />
                    </>
                  )}
                </CategoryGroup>
              );
            })
          )}
        </OptionWrapper>
      )}
    </SelectContainer>
  );
}
